import { useState, useEffect, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Maximize, Image as ImageIcon, RotateCcw, RotateCw, Loader2 } from 'lucide-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';

interface ImageLightboxProps {
  images: Array<{ id?: string; url: string; thumbnailUrl?: string; originalUrl?: string | null }>;
  initialIndex?: number;
  onClose: () => void;
  title?: string;
  itemId?: string; // Optional - enables rotation controls when provided
}

export function ImageLightbox({ images, initialIndex = 0, onClose, title, itemId }: ImageLightboxProps) {
  const queryClient = useQueryClient();
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [showingOriginal, setShowingOriginal] = useState(false);
  const [originalLoading, setOriginalLoading] = useState(false);

  const currentImage = images[currentIndex];
  const hasOriginal = !!currentImage?.originalUrl;
  const canRotate = !!itemId && !!currentImage?.id;

  // Rotation mutation
  const rotateImageMutation = useMutation({
    mutationFn: ({ imageId, direction }: { imageId: string; direction: 'left' | 'right' }) =>
      api.items.rotateImage(itemId!, imageId, direction),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['items'] });
    },
  });

  const MIN_ZOOM = 1;
  const MAX_ZOOM = 5;
  const ZOOM_STEP = 0.5;

  // Reset zoom and original view when image changes
  useEffect(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
    setShowingOriginal(false);
  }, [currentIndex]);

  // Toggle original image view
  const toggleOriginal = useCallback(() => {
    if (!hasOriginal) return;
    if (!showingOriginal) {
      setOriginalLoading(true);
    }
    setShowingOriginal(!showingOriginal);
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, [hasOriginal, showingOriginal]);

  // Handle original image load
  const handleOriginalLoad = useCallback(() => {
    setOriginalLoading(false);
  }, []);

  const navigateImage = useCallback((direction: 'prev' | 'next') => {
    if (images.length <= 1) return;
    setCurrentIndex((prev) => {
      if (direction === 'prev') {
        return prev > 0 ? prev - 1 : images.length - 1;
      }
      return prev < images.length - 1 ? prev + 1 : 0;
    });
  }, [images.length]);

  const handleZoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const handleZoomOut = useCallback(() => {
    setZoom((prev) => {
      const newZoom = Math.max(prev - ZOOM_STEP, MIN_ZOOM);
      if (newZoom === MIN_ZOOM) {
        setPosition({ x: 0, y: 0 });
      }
      return newZoom;
    });
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      } else if (e.key === 'ArrowLeft') {
        navigateImage('prev');
      } else if (e.key === 'ArrowRight') {
        navigateImage('next');
      } else if (e.key === '+' || e.key === '=') {
        handleZoomIn();
      } else if (e.key === '-') {
        handleZoomOut();
      } else if (e.key === '0') {
        resetZoom();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, navigateImage, handleZoomIn, handleZoomOut, resetZoom]);

  // Double click to zoom
  const handleDoubleClick = () => {
    if (zoom > 1) {
      resetZoom();
    } else {
      setZoom(2.5);
    }
  };

  // Mouse handlers for panning
  const handleMouseDown = (e: React.MouseEvent) => {
    if (zoom > 1) {
      e.preventDefault();
      setIsDragging(true);
      setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    }
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // Touch handlers for pinch zoom and pan
  const [lastTouchEnd, setLastTouchEnd] = useState(0);
  const [initialPinchDistance, setInitialPinchDistance] = useState<number | null>(null);
  const [initialPinchZoom, setInitialPinchZoom] = useState(1);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (e.touches.length === 2) {
      // Pinch start
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (touch1 && touch2) {
        const distance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        setInitialPinchDistance(distance);
        setInitialPinchZoom(zoom);
      }
    } else if (e.touches.length === 1 && zoom > 1) {
      // Pan start
      const touch = e.touches[0];
      if (touch) {
        setIsDragging(true);
        setDragStart({
          x: touch.clientX - position.x,
          y: touch.clientY - position.y,
        });
      }
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (e.touches.length === 2 && initialPinchDistance !== null) {
      // Pinch zoom
      const touch1 = e.touches[0];
      const touch2 = e.touches[1];
      if (touch1 && touch2) {
        const currentDistance = Math.hypot(
          touch2.clientX - touch1.clientX,
          touch2.clientY - touch1.clientY
        );
        const scale = currentDistance / initialPinchDistance;
        const newZoom = Math.min(Math.max(initialPinchZoom * scale, MIN_ZOOM), MAX_ZOOM);
        setZoom(newZoom);

        if (newZoom === MIN_ZOOM) {
          setPosition({ x: 0, y: 0 });
        }
      }
    } else if (e.touches.length === 1 && isDragging && zoom > 1) {
      // Pan
      const touch = e.touches[0];
      if (touch) {
        setPosition({
          x: touch.clientX - dragStart.x,
          y: touch.clientY - dragStart.y,
        });
      }
    }
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (e.touches.length === 0) {
      // Check for double tap
      const now = Date.now();
      if (now - lastTouchEnd < 300) {
        handleDoubleClick();
      }
      setLastTouchEnd(now);

      setIsDragging(false);
      setInitialPinchDistance(null);
    }
  };

  if (!currentImage) return null;

  return (
    <div className="fixed inset-0 z-[60] bg-black flex flex-col">
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 sm:p-4 text-white bg-black/80 z-20"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 sm:gap-4">
          {images.length > 1 && (
            <span className="text-xs sm:text-sm bg-white/20 px-2 sm:px-3 py-1 rounded-full">
              {currentIndex + 1} / {images.length}
            </span>
          )}
          {title && (
            <span className="text-xs sm:text-sm truncate max-w-[120px] sm:max-w-[300px]">{title}</span>
          )}
        </div>

        {/* Zoom controls */}
        <div className="flex items-center gap-1 sm:gap-2">
          <button
            onClick={handleZoomOut}
            disabled={zoom <= MIN_ZOOM}
            className="p-2 sm:p-2.5 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30 touch-manipulation"
            title="Zoom out"
          >
            <ZoomOut className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          <span className="text-xs sm:text-sm min-w-[45px] sm:min-w-[55px] text-center font-medium">
            {Math.round(zoom * 100)}%
          </span>

          <button
            onClick={handleZoomIn}
            disabled={zoom >= MAX_ZOOM}
            className="p-2 sm:p-2.5 hover:bg-white/20 rounded-full transition-colors disabled:opacity-30 touch-manipulation"
            title="Zoom in"
          >
            <ZoomIn className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {zoom > 1 && (
            <button
              onClick={resetZoom}
              className="p-2 sm:p-2.5 hover:bg-white/20 rounded-full transition-colors ml-1 touch-manipulation"
              title="Reset zoom"
            >
              <Maximize className="w-5 h-5 sm:w-6 sm:h-6" />
            </button>
          )}

          {/* View Full Resolution button - only show if original exists */}
          {hasOriginal && (
            <button
              onClick={toggleOriginal}
              className={`flex items-center gap-1.5 px-2.5 sm:px-3 py-1.5 sm:py-2 rounded-full transition-colors ml-2 touch-manipulation text-xs sm:text-sm font-medium ${
                showingOriginal
                  ? 'bg-emerald-500 text-white'
                  : 'bg-white/20 hover:bg-white/30 text-white'
              }`}
              title={showingOriginal ? 'View optimized image' : 'View full resolution'}
            >
              <ImageIcon className="w-4 h-4" />
              <span className="hidden sm:inline">{showingOriginal ? 'Optimized' : 'Full Res'}</span>
            </button>
          )}

          <button
            onClick={onClose}
            className="p-2 sm:p-2.5 hover:bg-white/20 rounded-full transition-colors ml-2 sm:ml-4 touch-manipulation"
            title="Close"
          >
            <X className="w-6 h-6 sm:w-7 sm:h-7" />
          </button>
        </div>
      </div>

      {/* Main image area */}
      <div
        className="flex-1 flex items-center justify-center overflow-hidden relative touch-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onDoubleClick={handleDoubleClick}
        style={{
          cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'zoom-in',
          touchAction: 'none',
        }}
      >
        {/* Navigation arrows */}
        {images.length > 1 && (
          <>
            <button
              onClick={(e) => { e.stopPropagation(); navigateImage('prev'); }}
              className="absolute left-2 sm:left-4 p-2 sm:p-3 text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10 touch-manipulation"
            >
              <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); navigateImage('next'); }}
              className="absolute right-2 sm:right-4 p-2 sm:p-3 text-white bg-black/50 hover:bg-black/70 rounded-full transition-colors z-10 touch-manipulation"
            >
              <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
            </button>
          </>
        )}

        {/* Loading indicator for original image */}
        {originalLoading && showingOriginal && (
          <div className="absolute inset-0 flex items-center justify-center z-20 bg-black/50">
            <div className="flex flex-col items-center gap-3">
              <div className="w-10 h-10 border-4 border-white/30 border-t-white rounded-full animate-spin" />
              <span className="text-white text-sm">Loading full resolution...</span>
            </div>
          </div>
        )}

        {/* Image with rotation buttons */}
        <div className="relative">
          {/* Rotation buttons - overlaid on image */}
          {canRotate && (
            <>
              {/* Rotate left - top left */}
              <button
                onClick={(e) => { e.stopPropagation(); currentImage?.id && rotateImageMutation.mutate({ imageId: currentImage.id, direction: 'left' }); }}
                disabled={rotateImageMutation.isPending}
                className="absolute top-2 left-2 w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center z-10 touch-manipulation disabled:opacity-50"
                title="Rotate left 90°"
              >
                {rotateImageMutation.isPending ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <RotateCcw className="w-5 h-5" />
                )}
              </button>
              {/* Rotate right - top right */}
              <button
                onClick={(e) => { e.stopPropagation(); currentImage?.id && rotateImageMutation.mutate({ imageId: currentImage.id, direction: 'right' }); }}
                disabled={rotateImageMutation.isPending}
                className="absolute top-2 right-2 w-11 h-11 bg-black/50 hover:bg-black/70 text-white rounded-full flex items-center justify-center z-10 touch-manipulation disabled:opacity-50"
                title="Rotate right 90°"
              >
                <RotateCw className="w-5 h-5" />
              </button>
            </>
          )}
          <img
            src={showingOriginal && currentImage.originalUrl ? currentImage.originalUrl : currentImage.url}
            alt={title || `Image ${currentIndex + 1}`}
            className="max-h-[calc(100vh-160px)] max-w-full object-contain select-none"
            style={{
              transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
              transition: isDragging ? 'none' : 'transform 0.1s ease-out',
            }}
            draggable={false}
            onLoad={showingOriginal ? handleOriginalLoad : undefined}
          />
        </div>

        {/* Zoom hint */}
        {zoom === 1 && !showingOriginal && (
          <div className="absolute bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 text-white/70 text-xs bg-black/60 px-3 py-1.5 rounded-full pointer-events-none">
            Double-tap to zoom • Pinch to zoom
          </div>
        )}

        {/* Full resolution indicator */}
        {showingOriginal && !originalLoading && (
          <div className="absolute bottom-20 sm:bottom-4 left-1/2 -translate-x-1/2 text-emerald-400 text-xs bg-black/60 px-3 py-1.5 rounded-full pointer-events-none flex items-center gap-1.5">
            <ImageIcon className="w-3.5 h-3.5" />
            Viewing full resolution
          </div>
        )}
      </div>

      {/* Thumbnail strip */}
      {images.length > 1 && (
        <div className="p-2 sm:p-3 flex justify-center gap-1.5 sm:gap-2 overflow-x-auto bg-black/80">
          {images.map((img, idx) => (
            <button
              key={img.id || idx}
              onClick={() => setCurrentIndex(idx)}
              className={`w-12 h-12 sm:w-14 sm:h-14 flex-shrink-0 rounded-lg overflow-hidden border-2 transition-all touch-manipulation ${
                idx === currentIndex
                  ? 'border-white opacity-100'
                  : 'border-transparent opacity-50 hover:opacity-80'
              }`}
            >
              <img
                src={img.thumbnailUrl || img.url}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
                draggable={false}
              />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
